import React, { useContext } from "react";
import { Text } from "@acko-tech/building-blocks.ui.typography";
import { H5 } from "@acko-tech/building-blocks.ui.typography";
import SelectButton from "@acko-tech/building-blocks.ui.select-button";
import { Row, Col } from "@acko-tech/building-blocks.ui.layout";

import { styled } from "@acko-tech/building-blocks.ui.common";
import Button from "@acko-tech/building-blocks.ui.button-old";
import {
  $fontSize,
  $neutralColors,
  $space,
  $white,
  $fontWeightNormal,
  $primaryColors
} from "@acko-tech/building-blocks.ui.common";
import { Box, Flex } from "@acko-tech/building-blocks.ui.common";
import Context from "@components/useContext/context";
import {
  $primaryColorsV2,
  StickyContainer,
  $grayLighter
} from "@components/car/journey/mobile/styles";
import DOMPurify from "isomorphic-dompurify";

interface StyledSelectInterface {
  selected: boolean;
  hasSubtext: boolean;
}

const StyledSelect = styled(SelectButton)`
  && {
    margin-bottom: 16px;
    padding: 1em 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: row-reverse;
    background-color: ${props => {
      let color = props.disabled ? $white : $grayLighter;
      if (props.selected) color = $primaryColorsV2.lightest;
      return color;
    }};
    border: ${props => {
      let color = props.disabled ? "#E0E2EB" : "";
      color = props.selected ? $primaryColorsV2.normal : color;
      return color !== "" ? `1px solid ${color}` : "none";
    }};
    color: ${(props: StyledSelectInterface) => `
    ${props.selected ? $neutralColors.dark : "#555A68"};
    ${props.hasSubtext ? "padding-left: 1em" : ""}
  `};
    ${props =>
      props.disabled &&
      `
    color: rgb(137, 144, 161);
    cursor: not-allowed;
  `}
  width: ${props => (props.isFullWidth ? "100%" : "")};
  }
`;

const StyledButton = styled(Button)`
  ${props =>
    props.isDesktop
      ? ` width: 45%; margin-top: 1rem;
           margin-left: 0.5rem;`
      : `margin: .5rem auto 0;`};
`;

const StyledBox = styled(Box)`
  white-space: pre-wrap;
  display: flex;
  justify-content: center;
`;

const Heading = styled(H5)`
  ${props => props.questionStyles};
  text-align: ${props => (props.isDesktop ? "left" : "left")};
  font-size: ${$fontSize.small};
  @media (min-width: 768px) {
    font-size: ${$fontSize.large};
    ${props => props.questionStyles};
  }
`;

const DescriptionText = styled(Text)`
  font-size: ${$fontSize.xs};
  color: ${$neutralColors.dark}
  text-align: ${props => (props.isDesktop ? "left" : "left")};
  margin-top: ${props => (props.isDesktop ? `1rem` : "0.625rem")};
`;

const MiniOptionWrapper = styled(Flex)`
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: initial;
    margin-left: -8px;
  }
`;

const FullWidthOptionWrapper = styled(Flex)`
  flex-direction: column;
`;

const Subtext = styled(Text)`
  font-size: ${$fontSize.xxs};
  color: #8990a1;
  margin-left: ${$space.s};
  flex: 1;
`;

const HeadingContainer = styled(Box)`
  margin-bottom: 36px;
  ${props => props.isDesktop && `width: 85%`};
`;

const Error = styled(Text)`
  color: rgb(253, 75, 68);
  font-size: 12px;
  position: relative;
  top: -5px;
  ${props => (props.isDesktop ? `margin-left: 8px;` : `text-align: center;`)}
`;

// TODO: TS errors
function Questions({
  question,
  options,
  fullWidthOptions,
  description,
  selectedValue,
  handleClick,
  questionStyles,
  columnBreak,
  showButton,
  buttonOnClick,
  showError,
  errorMessage,
  isBike,
  selectedList,
  innerContainerStyles
}: Props): JSX.Element {
  const { isDesktop } = useContext(Context);

  const layoutBreakCount = isDesktop ? 2 : 4;
  const colBreak = columnBreak || layoutBreakCount;

  const renderSelectButton = (
    item: any,
    isFullWidth: boolean = false
  ): JSX.Element => {
    const Icon = item.icon;

    const isSelected = selectedList
      ? selectedList.includes(item.name)
      : selectedValue === item.name;
    return (
      <StyledSelect
        hideSelectIcon
        key={item.name}
        selected={isSelected}
        handleClick={() => {
          if (!item.disabled) handleClick(item);
        }}
        value={item.name}
        id={item.buttonId}
        hasSubtext={Boolean(item.subtext)}
        disabled={item.disabled}
        theme={{
          primaryColor: $primaryColors,
          normalWeight: $fontWeightNormal
        }}
        isBike={isBike}
        isDesktop={isDesktop}
        isFullWidth={isFullWidth}
      >
        <StyledBox colBreak={colBreak} hasIcon={Icon}>
          <div
            style={{ fontSize: "14px", fontWeight: "400", lineHeight: "20px" }}
          >
            {item.name}
          </div>
          {item.subtext && <Subtext>{item.subtext}</Subtext>}
        </StyledBox>
        {Icon && <Icon />}
      </StyledSelect>
    );
  };

  const renderOption = (option: Options, colBreak: number): JSX.Element => {
    return (
      <Col key={option.name.toString()} xxs={1 / colBreak}>
        {renderSelectButton(option)}
      </Col>
    );
  };

  return (
    <>
      {(question || description) && (
        <HeadingContainer isDesktop={isDesktop}>
          <>
            {question && (
              <Heading isDesktop={isDesktop} questionStyles={questionStyles}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(question)
                  }}
                />
              </Heading>
            )}
            {description && (
              <DescriptionText isDesktop={isDesktop}>
                {description}
              </DescriptionText>
            )}
          </>
        </HeadingContainer>
      )}
      <MiniOptionWrapper>
        {options?.length <= layoutBreakCount &&
          options.map(
            (option): JSX.Element =>
              isDesktop
                ? renderOption(option, colBreak)
                : renderSelectButton(option)
          )}
      </MiniOptionWrapper>

      {isDesktop ? (
        <Flex>
          <Row style={{ width: "100%" }}>
            {options?.length > layoutBreakCount &&
              options.map(
                (option): JSX.Element => {
                  const columnBreak =
                    colBreak ||
                    (options.length > layoutBreakCount && options.length < 8
                      ? 2
                      : 4);
                  return renderOption(option, columnBreak);
                }
              )}
          </Row>
        </Flex>
      ) : (
        <Flex>
          <Row style={innerContainerStyles || {}}>
            {options?.length > layoutBreakCount &&
              options.map((option): JSX.Element => renderOption(option, 2))}
          </Row>
        </Flex>
      )}
      <FullWidthOptionWrapper>
        {Boolean(fullWidthOptions?.length) &&
          fullWidthOptions!.map(
            (option): JSX.Element => renderSelectButton(option, true)
          )}
      </FullWidthOptionWrapper>

      {showError && <Error isDesktop={isDesktop}>{errorMessage}</Error>}

      {showButton && (
        <StickyContainer nonSticky={isDesktop}>
          <StyledButton
            isDesktop={isDesktop}
            styles={{
              colorScheme: $primaryColorsV2
            }}
            fluid={!isDesktop}
            onClick={() => {
              if (buttonOnClick) buttonOnClick();
            }}
          >
            Continue
          </StyledButton>
        </StickyContainer>
      )}
    </>
  );
}

interface Props {
  question?: string;
  description?: string;
  options: Options[];
  fullWidthOptions?: Options[];
  selectedValue?: string;
  handleClick(value: any): void;
  questionStyles?: any;
  isDesktop?: boolean;
  columnBreak?: number;
  showButton?: boolean;
  buttonOnClick?: Function;
  showError?: boolean;
  errorMessage?: string;
  isBike?: boolean;
  selectedList?: any;
  innerContainerStyles?: any;
}

interface Options {
  name: string | number | boolean;
  disabled?: boolean;
}

export default Questions;
